import axios from "axios"
import { env } from "@/utils/env"
// import type { AppStore } from "@/app/store"
import type {
  CampingSiteMapResponse,
  CampingSiteSearchRequest,
  Page,
  CampingSite,
  CampingSiteResponse,
  PostTagResponse,
  BoardResponse,
  SlicePostSimpleResponse,
  PostDetailResponse,
  PageCommentResponse,
  CommentCreateRequest,
  LikeResponse,
} from "@/types/domain"
import type { ReviewResponse, ReviewCreateRequest, ReviewUpdateRequest, PageReviewResponse } from "@/types/review"

const api = axios.create({
  baseURL: env.apiUrl,
  headers: {
    "Content-Type": "application/json",
  },
})
// ... (skip unchanged parts)




export const searchCampingSites = async (
  request: CampingSiteSearchRequest,
): Promise<Page<CampingSiteMapResponse>> => {
  const response = await api.post<Page<CampingSiteMapResponse>>(
    "/api/camping-sites/search",
    request,
  )
  return response.data
}

export const searchCampingSitesForAdmin = async (
  request: CampingSiteSearchRequest,
): Promise<Page<CampingSiteMapResponse>> => {
  const response = await api.post<Page<CampingSiteMapResponse>>(
    "/api/camping-sites/search/admin",
    request,
  )
  return response.data
}

export const getPendingCampingSitesForAdmin = async (
  page: number = 0,
  size: number = 100,
  name?: string,
  tagIds?: number[],
): Promise<Page<CampingSiteResponse>> => {
  const response = await api.get<Page<CampingSiteResponse>>(
    "/api/admin/camping-sites/pending",
    {
      params: {
        page,
        size,
        name,
        tagIds: tagIds?.join(","), // or use paramsSerializer depending on backend expectations
      },
    },
  )
  return response.data
}

export const getSiteById = async (id: string): Promise<CampingSite> => {
  const response = await api.get<CampingSiteResponse>(`/api/camping-sites/${id}`)

  // API 응답을 프론트엔드 타입으로 변환
  const apiData = response.data
  const campingSite: CampingSite = {
    id: apiData.id,
    name: apiData.name,
    address: apiData.address,
    description: apiData.description,
    latitude: apiData.latitude,
    longitude: apiData.longitude,
    phoneNumber: apiData.phoneNumber,
    imageUrls: apiData.images.map(img => img.url),
    imageIds: apiData.images.map(img => img.id),
    tags: apiData.tags.map(tag => ({
      id: tag.id,
      name: tag.name,
      icon: tag.icon,
      tagGroup: {
        id: tag.tagGroup,
        name: tag.tagGroupTitle,
      },
    })),
    // TODO: API 응답 확정 후 필드 추가
    // lineIntro: apiData.lineIntro,
    // intro: apiData.intro,
    // features: apiData.features,
    // theme: apiData.theme,
    // animal: apiData.animal,
    // operationType: apiData.operationType,
    // operationDays: apiData.operationDays,
    // facilities: apiData.facilities,
    exposureStatus: apiData.exposureStatus,
    referralLinks: apiData.referralLinks,
  }

  return campingSite
}

export const approveSite = async (id: string): Promise<void> => {
  await api.patch(`/api/camping-sites/${id}/approve`)
}

export const updateCampingSiteStatus = async (
  id: string,
  status: "PENDING" | "APPROVED" | "REJECTED",
): Promise<void> => {
  await api.patch(`/api/admin/camping-sites/${id}/status`, { status })
}

// Community API

export type PostCreateRequest = {
  boardId?: string
  title: string
  content: string
  tagNames: string[]
  imageIds: number[]
  campingSiteIds: string[]
}

export type ImageMetadataResponse = {
  imageId: number
  originalFileName: string
  fullImageUrl: string
  fullThumbnailUrl: string
  latitude: number
  longitude: number
  address: string
  takenAt: string
}

export const getBoards = async (): Promise<BoardResponse[]> => {
  const response = await api.get<BoardResponse[]>("/api/community/boards")
  return response.data
}

export const uploadCommunityImage = async (file: File): Promise<ImageMetadataResponse> => {
  const formData = new FormData()
  formData.append("file", file)
  const response = await api.post<ImageMetadataResponse>("/api/community/images", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
  return response.data
}

export const getPosts = async (
  tags?: string[],
  lastId?: string,
  size: number = 10,
): Promise<SlicePostSimpleResponse> => {
  const response = await api.get<SlicePostSimpleResponse>(
    "/api/community/posts",
    {
      params: {
        tags,
        lastId,
        size,
      },
      paramsSerializer: {
        indexes: null, // tags[0]=a&tags[1]=b 형태가 아닌 tags=a&tags=b 형태로 전송
      },
    },
  )
  return response.data
}

export const getPostTags = async (
  keyword?: string,
): Promise<PostTagResponse[]> => {
  const response = await api.get<PostTagResponse[]>("/api/community/tags", {
    params: { keyword },
  })
  return response.data
}

export const createPost = async (request: PostCreateRequest): Promise<void> => {
  await api.post("/api/community/posts", request)
}

export const getPostDetail = async (postId: string): Promise<PostDetailResponse> => {
  const response = await api.get<PostDetailResponse>(`/api/community/posts/${postId}`)
  return response.data
}

export const getComments = async (
  postId: string,
  page: number = 0,
  size: number = 20,
): Promise<PageCommentResponse> => {
  const response = await api.get<PageCommentResponse>(
    `/api/community/posts/${postId}/comments`,
    {
      params: { page, size },
    },
  )
  return response.data
}

export const createComment = async (
  postId: string,
  request: CommentCreateRequest,
): Promise<void> => {
  await api.post(`/api/community/posts/${postId}/comments`, request)
}

export const togglePostLike = async (postId: string): Promise<LikeResponse> => {
  const response = await api.post<LikeResponse>(`/api/community/posts/${postId}/likes`)
  return response.data
}

export const toggleCommentLike = async (commentId: string): Promise<LikeResponse> => {
  const response = await api.post<LikeResponse>(
    `/api/community/comments/${commentId}/likes`,
  )
  return response.data
}

// Review API
export const getReviews = async (
  siteId: string,
  page = 0,
  size = 10,
): Promise<PageReviewResponse> => {
  const response = await api.get<PageReviewResponse>(
    `/api/reviews/camping-sites/${siteId}`,
    {
      params: { page, size, sort: "createdAt,desc" },
    },
  )
  return response.data
}

export const createReview = async (request: ReviewCreateRequest): Promise<ReviewResponse> => {
  const response = await api.post<ReviewResponse>("/api/reviews", request)
  return response.data
}

export const updateReview = async (
  reviewId: string,
  request: ReviewUpdateRequest,
): Promise<ReviewResponse> => {
  const response = await api.patch<ReviewResponse>(`/api/reviews/${reviewId}`, request)
  return response.data
}

export const deleteReview = async (reviewId: string): Promise<void> => {
  await api.delete(`/api/reviews/${reviewId}`)
}

export const getReviewsByUserId = async (
  userId: number,
  page = 0,
  size = 10,
): Promise<PageReviewResponse> => {
  const response = await api.get<PageReviewResponse>(
    `/api/reviews/users/${userId.toString()}`,
    {
      params: { page, size, sort: "createdAt,desc" },
    },
  )
  return response.data
}

export const getReviewById = async (reviewId: string): Promise<ReviewResponse> => {
  const response = await api.get<ReviewResponse>(`/api/reviews/${reviewId}`)
  return response.data
}

export default api
